/* eslint-disable @typescript-eslint/naming-convention */
import type { EventDataInterface } from '@nextgen-web-framework/core';
import type { Cart } from '@commerce-cart-webcomponents/sdk';
import type { SelectedProducts } from '@commerce-promotion-webcomponents/ui';
import type { Products } from '@commerce-product-webcomponents/sdk';
import type {
  Cycle,
  ProfileResponse,
  PlanResponse,
  SubscriptionResponse,
  Subscriptions,
  SubscriptionType,
  SubscriptionProductEntry,
  ProductData,
} from '../';
import { ServiceLocation } from '../services/models/ServiceLocation';
import { Profiles } from '../services';

// create models ........................
export interface Forbidden {
  /**
   *
   * @type {string}
   * @memberof Forbidden
   */
  code?: string;
  /**
   *
   * @type {string}
   * @memberof Forbidden
   */
  message?: string;
}

export interface SelectedSubscriptionPlan {
  planId?: string;
  plan?: PlanResponse | {};
  cycle?: Cycle;
  paymentDate?: string;
  tncAccepted?: boolean;
}
//...................................

export type InitialSubscriptionState = {
  isLoading: boolean;
  hasError: boolean;
  error: Forbidden;
  selectedSubscriptionPlan: SelectedSubscriptionPlan;
  currentView?: string;
  tncAccepted?: boolean;
  profile?: ProfileResponse | {};
  subscriptionDetails?: SubscriptionResponse | {};
  subscriptions?: Subscriptions;
  //profiles?: Array<ProfileResponse>;
  profilesRes?: Profiles;
  profileDetails?: ProfileResponse | {};
  plans?: Array<PlanResponse>;
  bundleState?: Array<BundleSlotType>;
  productType?: string;
  productId?: string;
  planDetails?: PlanResponse | {};
  bundleType?: string;
  enableSubscription?: boolean;
  showGcoConfirmationModal: boolean;
  newSubscriptionProductList?: Array<{
    productId: number;
    productTitle: string;
    maxQuantityLimit?: number;
    minQuantityLimit?: number;
  }>;
  subscriptionsCmsData: Array<SubcriptionPlanCMSResponseType>;
  cartDetails?: Cart;
  subscriptionPlanCMSId?: string;
  planOrderBenefitSelections?: {
    orderId: number;
    allBenefitsSelected: boolean;
    selectedProducts?: SelectedProducts;
  };
  products?: Products;
};

export type SampleIdReqPayloadType = {
  sampleId: string;
};
export type ProjectionReqPayloadType = {
  projections?: string;
};

export type SampleSubscribeEventType = {
  topics: string | string[];
  version: string;
  cb: (a: string, b: unknown) => void;
};

export type SampleEventPayloadType = {
  sampleId: string;
  [key: string]: string;
};

export type SamplePublishPayloadEventType = {
  payload: SampleEventPayloadType;
};

export type SamplePublishEventType = SamplePublishPayloadEventType & {
  event: string;
};

export enum SampleEventsVersionsEnum {
  '1.0.0' = '1.0.0',
}

export type BaseUrlReqPayloadType = {
  baseUrl: string;
};

export interface SampleEventData extends EventDataInterface {
  sampleId?: string;
}

export interface ISubscriptionConfirmation {
  id: string;
  subscriptionId: string;
  subscriptionProfile?: string;
  totalAmount: number;
  shippingDetails: {
    deliveryAddress?: string;
  };
  minimumNumberOfDays: number;
  maximumNumberOfDays: number;
  deliveryInstruction: string;
  deliveryOption: string;
  paidStatus: string;
  processDay: number;
  planDescription: string;
  userName: string;
  userEmail: string;
  userMobileNo: string;
  isDownline: boolean;
  amwayUserId?: string;
  paymentId?: string;
  subscriptionType?: SubscriptionType;
  products?: Array<ProductData>;
  entries?: Array<SubscriptionProductEntry>;
  nextPaymentDate?: Date;
}

export enum ComponentType {
  home_banner = 'home_banner',
  why_and_how = 'why_and_how',
  plans_list = 'plans_list',
  faqs = 'faqs',
}

export type BundleSlotEntriesType = {
  product: { id: string };
  quantity: number;
};

export type BundleSlotType = {
  id: string;
  entries: BundleSlotEntriesType[];
};

export type SubscriptionSummary = {
  totalItems: number;
  totalAmount: string;
  productEntries: Array<IProductEntries>;
  subTotal: string;
  shippingFee: string;
  freeShipping: boolean;
  taxes: {
    value: 0;
    taxDetails: [];
  };
  placeOrderDisabled: boolean;
};

export interface IProductEntries {
  brandName: string;
  productName: string;
  productId: string;
  varient: string;
  currency: string;
  productPrice: number;
  link: string;
  productQuantity: number;
  alt?: string;
  strikethroughPrice?: number;
  pointValue?: string;
  businessVolume?: string;
}

export interface Tax {
  value?: number;
  taxDetails?: Array<TaxDetail>;
}

export interface TaxDetail {
  absolute?: number;
  appliedValue?: number;
  code?: string;
  value?: number;
  taxableAmount?: number;
}

export type PlanDetailsTopBanner = {
  display_title: string;
  description: string;
  bannerImages: Array<Image>;
  footerImages: Array<Image>;
  backgroundColor: string;
  fontColor: string;
};

export interface Image {
  url: string;
  alt_text: string;
  viewport: string[];
}

export type StartSubscription = {
  display_title: string;
  description: string;
  plan_codes: {
    primary_plan_code: string;
    secondary_plan_codes: string;
  };
  rewards_and_benefits: {
    display_title: string;
    details: string;
  };
};

export type PlanBriefDescriptionDetails = {
  displayTitle: string;
  shortDescription: string;
  longDescription: string;
  images?: Array<Image>;
};

export type PlanTagDetails = {
  _content_type_uid?: string;
  uid?: string;
  title?: string;
  tag_icon: {
    faicon: string;
  };
  tag_label: string;
  tags?: [];
  theme?: string;
  locale?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  ACL?: {};
  _version?: number;
  _in_progress?: boolean;
  publish_details?: {
    time: string;
    user: string;
    environment: string;
    locale: string;
  };
};

export interface SubcriptionPlanCMSResponseType {
  _version: number;
  locale: string;
  uid: string;
  ACL: {};
  _in_progress: boolean;
  benefits_description: string;
  created_at: string;
  created_by: string;
  frequency_and_length_description: string;
  long_description: string;
  plan_code: string;
  plan_tag?: PlanTagDetails[];
  plan_name?: string;
  plan_image?: {
    images: [
      {
        viewport: string;
        image: {
          url: string;
        };
      },
    ];
  };
  short_description: string;
  tags: string[];
  terms_and_conditions: string;
  title: string;
  updated_at: string;
  updated_by: string;
  publish_details: {
    environment: string;
    locale: string;
    time: string;
    user: string;
  };
}

export type CustomRequestOptions = {
  headers?: {
    [key: string]: string;
  };
  baseURL?: string;
};

export enum FulfillmentTypeIdEnum {
  HOME_DELIVERY = 'homedelivery',
  BOPIS = 'bopis',
}

type HomeAddress = {
  contact?: string;
  address?: string;
  deliveryInstructions?: string;
  phone?: string;
  country?: string;
};

type StoreAddress = {
  serviceLocation?: ServiceLocation;
};

export type FulfillmentType = {
  id?: FulfillmentTypeIdEnum;
  homeAddress?: HomeAddress;
  storeAddress?: StoreAddress;
};
